import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, LineChart,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from 'recharts';
import {
  RefreshCw, Layers, AlertCircle, BarChart3,
  TrendingUp, TrendingDown, AlertTriangle,
} from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import useLegendToggle from '../../utils/useLegendToggle';

/**
 * M5 SEU Analytics — joins ERS SEU design data (ers_seu_electrical, ers_seu_fuel)
 * with OIM measured actuals to surface:
 *   • Pareto ranking of SEUs by designed annual kWh-eq
 *   • Baseline (designed, period-scaled) vs Actual (OIM-measured)
 *   • Savings or overrun per fuel type
 *   • Daily actual trend by energy_type
 *   • Opportunity flags (>110% or <70% utilisation vs design baseline)
 */

const PERIOD_OPTIONS = [
  { id: '7d',   label: 'Last 7 days' },
  { id: '30d',  label: 'Last 30 days' },
  { id: '90d',  label: 'Last 90 days' },
  { id: '180d', label: 'Last 180 days' },
  { id: '365d', label: 'Last 365 days' },
];

// Consistent palette across charts
const ENERGY_TYPE_COLOR = {
  electricity:  '#f59e0b',
  diesel:       '#dc2626',
  petrol:       '#ea580c',
  natural_gas:  '#0ea5e9',
  gas:          '#0ea5e9',
  lpg:          '#7c3aed',
  furnace_oil:  '#991b1b',
  coal:         '#374151',
  bagasse:      '#16a34a',
  biomass:      '#65a30d',
  husk:         '#a16207',
  rice_husk:    '#a16207',
  wood:         '#854d0e',
  spent_wash:   '#9333ea',
  steam:        '#0891b2',
};
const FALLBACK = ['#3b82f6', '#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#dc2626', '#0ea5e9'];

const fmt = (n, d = 2) => n == null || isNaN(n) ? '—'
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(2) + 'K';
  return v.toFixed(0);
};

export default function OimSEUAnalyticsPanel({ siteId }) {
  const { showError } = useAlert();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Click-to-toggle legend state — separate buckets per chart.
  const paretoLegend = useLegendToggle();
  const trendLegend = useLegendToggle();

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: siteId, period });
      const res = await axios.get(`${API}/oim/seu-analytics?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.detail || 'Failed to load SEU analytics');
    } finally { setLoading(false); }
  }, [siteId, period, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const colorFor = (t, idx = 0) => ENERGY_TYPE_COLOR[String(t).toLowerCase()] || FALLBACK[idx % FALLBACK.length];

  const paretoChartData = useMemo(() => {
    return (data?.seu_pareto || []).slice(0, 15).map((s, i) => ({
      name: s.name?.length > 20 ? s.name.slice(0, 20) + '…' : s.name,
      fullName: s.name,
      annual_kwh: s.annual_kwh,
      cum_pct:    s.cum_pct,
      is_seu:     s.is_seu,
      fuel_type:  s.fuel_type,
      rank:       s.rank_global,
    }));
  }, [data]);

  if (!siteId) return (
    <div className="p-12 text-center text-gray-500">
      <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-2"/>
      Select a site to view SEU analytics.
    </div>
  );

  const k = data?.kpis || {};
  const types = data?.energy_types_seen || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Layers className="w-5 h-5 text-purple-600"/> SEU Analytics
          </h2>
          <div className="text-xs text-gray-500">ERS-designed baseline vs OIM-measured actuals · Pareto · Opportunities</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="px-2 py-1.5 border rounded text-sm">
            {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button onClick={fetchData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="SEU Equipment" value={`${fmt(k.seu_count, 0)}`} sub={`${fmt(k.equipment_count, 0)} total tracked`} accent="violet" loading={loading}/>
        <Kpi label="Period Baseline" value={`${fmtCompact(k.total_period_baseline_kwh)} kWh`} sub={`${fmtCompact(k.total_designed_annual_kwh)} kWh designed/yr`} accent="blue" loading={loading}/>
        <Kpi label="Actual Consumed" value={`${fmtCompact(k.total_actual_kwh)} kWh`} sub={`${fmt(k.fuel_types_tracked, 0)} fuel types`} accent="amber" loading={loading}/>
        <Kpi
          label={k.total_savings_kwh >= 0 ? 'Savings vs Baseline' : 'Overrun vs Baseline'}
          value={`${fmtCompact(Math.abs(k.total_savings_kwh ?? 0))} kWh`}
          sub={k.savings_pct != null ? `${fmt(k.savings_pct, 1)}% ${k.savings_pct >= 0 ? 'under' : 'over'} design` : '—'}
          accent={k.total_savings_kwh >= 0 ? 'emerald' : 'rose'}
          loading={loading}
        />
      </div>

      {/* Pareto chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">SEU Pareto — top 15 by designed annual kWh-eq</h3>
        <p className="text-xs text-gray-500 mb-2">
          Bars = annual kWh-eq · Line = cumulative %. SEUs (≥80% cumulative) are typically the focus of energy management.
          Source: ERS desktop study (ers_seu_electrical + ers_seu_fuel).
        </p>
        {paretoChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={paretoChartData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} interval={0}/>
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={fmtCompact}/>
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} domain={[0, 100]}/>
              <Tooltip
                formatter={(v, name) => name === 'cum_pct' ? `${fmt(v, 1)}%` : `${fmt(v, 0)} kWh`}
                labelFormatter={(_, p) => p?.[0]?.payload?.fullName || ''}
              />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                onClick={paretoLegend.onLegendClick}
                formatter={paretoLegend.legendFormatter}
              />
              <Bar yAxisId="left" dataKey="annual_kwh" name="Annual kWh-eq" radius={[2, 2, 0, 0]}
                   hide={paretoLegend.isHidden('Annual kWh-eq')}>
                {paretoChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.is_seu ? '#7c3aed' : '#cbd5e1'}/>
                ))}
              </Bar>
              <Line yAxisId="right" type="monotone" dataKey="cum_pct" name="Cumulative %"
                    stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }}
                    hide={paretoLegend.isHidden('Cumulative %')}/>
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty hint="No ERS SEU data — run desktop study in ERS module to populate baseline equipment list"/>}
      </div>

      {/* Baseline vs Actual table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Baseline vs Actual by Fuel Type</h3>
        <p className="text-xs text-gray-500 mb-2">
          Period-scaled designed baseline (annual ÷ 365 × period_days) compared to OIM-measured actual consumption.
          Utilisation = actual ÷ baseline. Colours: green &lt;100%, amber 100-110%, red &gt;110%.
        </p>
        {(data?.baseline_vs_actual || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">Fuel Type</th>
                  <th className="px-3 py-2 text-right">Annual Baseline</th>
                  <th className="px-3 py-2 text-right">Period Baseline</th>
                  <th className="px-3 py-2 text-right">Actual</th>
                  <th className="px-3 py-2 text-right">Δ Savings</th>
                  <th className="px-3 py-2 text-right">Utilisation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.baseline_vs_actual.map(row => {
                  const u = row.utilisation_pct;
                  const utilColor = u == null ? 'text-gray-400'
                    : u > 110 ? 'text-rose-700 bg-rose-50'
                    : u > 100 ? 'text-amber-700 bg-amber-50'
                    : 'text-emerald-700 bg-emerald-50';
                  return (
                    <tr key={row.fuel_type} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-800 capitalize">{row.fuel_type}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{fmtCompact(row.annual_baseline_kwh)} kWh</td>
                      <td className="px-3 py-2 text-right text-gray-700">{fmtCompact(row.period_baseline_kwh)} kWh</td>
                      <td className="px-3 py-2 text-right text-gray-700">{fmtCompact(row.actual_kwh)} kWh</td>
                      <td className={`px-3 py-2 text-right font-semibold ${row.savings_kwh >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {row.savings_kwh >= 0 ? '+' : ''}{fmtCompact(row.savings_kwh)} kWh
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${utilColor}`}>
                          {u != null ? `${fmt(u, 1)}%` : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <Empty hint="Need both ERS baseline + OIM actuals to compare"/>}
      </div>

      {/* Daily actual trend */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Actual Energy by Type</h3>
        <p className="text-xs text-gray-500 mb-2">kWh-eq per day across {types.length || 0} energy types — flat lines = stable operations, spikes = batch / DG events.</p>
        {(data?.daily_actual_trend || []).length > 0 && types.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.daily_actual_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtCompact}/>
              <Tooltip formatter={(v, n) => [`${fmt(v, 0)} kWh`, n]}/>
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                onClick={trendLegend.onLegendClick}
                formatter={trendLegend.legendFormatter}
              />
              {types.map((t, i) => (
                <Line key={t} type="monotone" dataKey={t} stroke={colorFor(t, i)}
                      strokeWidth={1.8} dot={false}
                      hide={trendLegend.isHidden(t)}/>
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty hint="No OIM energy rows in period"/>}
      </div>

      {/* Opportunities */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500"/> Opportunities
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Auto-flagged based on baseline drift: &gt;110% (overrun) or &lt;70% (underutilised).
          Investigate before treating as savings.
        </p>
        {(data?.opportunities || []).length > 0 ? (
          <div className="space-y-2">
            {data.opportunities.map((op, i) => {
              const isOverrun = op.type === 'overrun';
              const sevColor = op.severity === 'high' ? 'border-rose-300 bg-rose-50'
                : op.severity === 'medium' ? 'border-amber-300 bg-amber-50'
                : 'border-blue-300 bg-blue-50';
              return (
                <div key={i} className={`border rounded-lg p-3 ${sevColor}`}>
                  <div className="flex items-start gap-2">
                    {isOverrun ? <TrendingUp className="w-4 h-4 text-rose-600 mt-0.5 shrink-0"/> : <TrendingDown className="w-4 h-4 text-blue-600 mt-0.5 shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-800 capitalize">
                        {op.fuel_type} · {op.type}
                        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${op.severity === 'high' ? 'bg-rose-200 text-rose-800' : op.severity === 'medium' ? 'bg-amber-200 text-amber-800' : 'bg-blue-200 text-blue-800'}`}>
                          {op.severity}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{op.message}</div>
                      <div className="text-[11px] text-gray-500 mt-1">
                        Δ {op.delta_kwh >= 0 ? '+' : ''}{fmtCompact(op.delta_kwh)} kWh vs baseline
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center text-gray-400 py-6 text-xs">
            <BarChart3 className="w-8 h-8 text-gray-300 mb-2"/>
            <div>No anomalies detected — actuals are within ±10/30% of design baseline.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = 'gray', loading }) {
  const accents = {
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700' },
    gray:    { bg: 'bg-gray-50',    text: 'text-gray-700' },
  };
  const a = accents[accent] || accents.gray;
  if (loading) return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg} animate-pulse`}>
      <div className="h-3 w-20 bg-gray-200 rounded mb-2"/>
      <div className="h-6 w-28 bg-gray-200 rounded"/>
    </div>
  );
  return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${a.text}`}>{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Empty({ hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-8 text-xs">
      <BarChart3 className="w-8 h-8 text-gray-300 mb-2"/>
      <div>{hint}</div>
    </div>
  );
}
